import React, { useEffect, useRef, useState } from 'react';
import { Settings } from 'lucide-react';

const EnhancedDrivingGame = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [speed, setSpeed] = useState(0);
  const [mode, setMode] = useState('helicopter');
  const [showSettings, setShowSettings] = useState(false);
  const [altitude, setAltitude] = useState(0);
  const [isOnGround, setIsOnGround] = useState(true);
  const [rotorAnimation, setRotorAnimation] = useState(false);
  const [viewMode, setViewMode] = useState('vehicle');
  const [isControllingVehicle, setIsControllingVehicle] = useState(false);
  const containerRef = useRef(null);
  const sceneRef = useRef(null);

  useEffect(() => {
    let scene, camera, renderer, controls;
    let helicopterModel, carModel, avatarModel, terrainMesh, waterMesh, landingPadMesh;
    let trees = [], clouds = [];
    let terrainHeightData = null;
    let animationId;
    let helicopterMixer = null;
    let carMixer = null;
    let avatarMixer = null;
    let clock = null;
    let avatarAnimations = [];
    let currentAvatarAction = null;

    // Helicopter
    let helicopterRotation = Math.PI;
    let pitch = 0;
    let roll = 0;
    let yaw = 0;
    let currentVerticalSpeed = 0;
    let targetVerticalSpeed = 0;
    let currentHorizontalSpeed = 0;
    let targetHorizontalSpeed = 0;
    let helicopterAltitude = 2;
    let isAirborne = false;
    let rotorSpeed = 0;
    let isRotorOn = false;

    // Car
    let carRotation = Math.PI;
    let currentCarSpeed = 0;
    let targetCarSpeed = 0;
    let carHeight = 0;

    // Avatar
    let avatarRotation = Math.PI;
    let currentAvatarSpeed = 0;
    let targetAvatarSpeed = 0;
    let isAvatarCrouching = false;
    let isAvatarJumping = false;
    let avatarJumpVelocity = 0;
    const GRAVITY = -0.05;
    const JUMP_FORCE = 0.15;
    const AVATAR_HEIGHT = 1.8;
    const AVATAR_CROUCH_HEIGHT = 1.2;

    const keys = {};

    // Load Three.js and addons
    const loadScripts = async () => {
      return new Promise((resolve) => {
        if (window.THREE && window.THREE.GLTFLoader) {
          resolve();
          return;
        }

        const threeScript = document.createElement('script');
        threeScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js';
        
        const orbitControlsScript = document.createElement('script');
        orbitControlsScript.src = 'https://cdn.jsdelivr.net/npm/three@0.128.0/examples/js/controls/OrbitControls.js';

        const gltfLoaderScript = document.createElement('script');
        gltfLoaderScript.src = 'https://unpkg.com/three@0.128.0/examples/js/loaders/GLTFLoader.js';

        threeScript.onload = () => {
          setTimeout(() => {
            orbitControlsScript.onload = () => {
              gltfLoaderScript.onload = () => {
                resolve();
              };
              document.head.appendChild(gltfLoaderScript);
            };
            document.head.appendChild(orbitControlsScript);
          }, 100);
        };
        
        document.head.appendChild(threeScript);
      });
    };

    const initScene = async () => {
      await loadScripts();
      
      const THREE = window.THREE;
      clock = new THREE.Clock();
      
      scene = new THREE.Scene();
      scene.fog = new THREE.Fog(0x87CEEB, 500, 3000);
      scene.background = new THREE.Color(0x6BB6FF);

      camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 5000);
      camera.position.set(0, 10, 20);

      renderer = new THREE.WebGLRenderer({ antialias: true });
      renderer.setSize(window.innerWidth, window.innerHeight);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      renderer.shadowMap.enabled = true;
      renderer.shadowMap.type = THREE.PCFSoftShadowMap;
      
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
        containerRef.current.appendChild(renderer.domElement);
      }

      document.body.style.overflow = 'hidden';
      document.body.style.margin = '0';
      document.body.style.padding = '0';

      sceneRef.current = { scene, camera, renderer };

      // Lighting
      const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
      scene.add(ambientLight);

      const directionalLight = new THREE.DirectionalLight(0xffffff, 0.9);
      directionalLight.position.set(50, 200, 100);
      directionalLight.castShadow = true;
      directionalLight.shadow.mapSize.width = 2048;
      directionalLight.shadow.mapSize.height = 2048;
      directionalLight.shadow.camera.left = -150;
      directionalLight.shadow.camera.right = 150;
      directionalLight.shadow.camera.top = 150;
      directionalLight.shadow.camera.bottom = -150;
      directionalLight.shadow.camera.near = 0.5;
      directionalLight.shadow.camera.far = 500;
      scene.add(directionalLight);

      const fillLight1 = new THREE.DirectionalLight(0xffffff, 0.3);
      fillLight1.position.set(-50, 100, -100);
      scene.add(fillLight1);

      const fillLight2 = new THREE.DirectionalLight(0xffffff, 0.25);
      fillLight2.position.set(100, 80, -50);
      scene.add(fillLight2);

      const hemisphereLight = new THREE.HemisphereLight(0x87CEEB, 0x3a5f40, 0.4);
      scene.add(hemisphereLight);

      const vehicleSpotlight = new THREE.PointLight(0xffffff, 0.8, 50);
      vehicleSpotlight.position.set(0, 20, 0);
      vehicleSpotlight.name = 'vehicleSpotlight';
      scene.add(vehicleSpotlight);

      createTerrain();
      createLandingPad();
      createWater();
      createTreesAndRocks(100);
      createClouds(50);

      loadHelicopterModel();
      loadCarModel();
      loadAvatarModel();
        
     // drag strip creation
      createDragStrip();

      setupOrbitControls();
      setupControls();

      setIsLoading(false);
      animate();

      window.addEventListener('resize', onWindowResize);
    };
      
      function createDragStrip() {
        const THREE = window.THREE;
        const dragStripGroup = new THREE.Group();
        
        // Location: 500 units SOUTH of helipad
        const stripX = -100;
        const stripZ = -600;
        const stripLength = 500;
        const stripWidth = 25;
        
        // Get terrain height at this location
        const terrainHeight = getTerrainHeight(stripX, stripZ);
        
        console.log('Creating drag strip at terrain height:', terrainHeight);
        
        // Position the entire drag strip group HIGHER than terrain
        dragStripGroup.position.set(stripX, terrainHeight + 0.5, stripZ); // +0.5 higher
        
        // 1. FIRST: Large flat concrete base
        const baseGeometry = new THREE.PlaneGeometry(stripWidth + 10, stripLength + 10);
        const baseMaterial = new THREE.MeshStandardMaterial({
          color: 0x666666,
          roughness: 0.8,
          metalness: 0.2,
          side: THREE.FrontSide // Changed
        });
        
        const flatBase = new THREE.Mesh(baseGeometry, baseMaterial);
        flatBase.rotation.x = -Math.PI / 2;
        flatBase.position.y = -0.5; // BELOW everything
        flatBase.receiveShadow = true;
        dragStripGroup.add(flatBase);
        
        // 2. SECOND: Asphalt strip
        const stripGeometry = new THREE.PlaneGeometry(stripWidth, stripLength);
        const stripMaterial = new THREE.MeshStandardMaterial({
          color: 0x222222,
          roughness: 0.9,
          metalness: 0.1,
          side: THREE.FrontSide // Changed
        });
        
        const asphaltStrip = new THREE.Mesh(stripGeometry, stripMaterial);
        asphaltStrip.rotation.x = -Math.PI / 2;
        asphaltStrip.position.y = 0; // On top of base
        asphaltStrip.receiveShadow = true;
        asphaltStrip.castShadow = false;
        dragStripGroup.add(asphaltStrip);
        
        // 3. THIRD: Yellow center line
        const centerLineGeometry = new THREE.PlaneGeometry(0.4, stripLength);
        const centerLineMaterial = new THREE.MeshBasicMaterial({
          color: 0xFFFF00,
          depthWrite: false,
          side: THREE.FrontSide // Changed
        });
        
        const centerLine = new THREE.Mesh(centerLineGeometry, centerLineMaterial);
        centerLine.rotation.x = -Math.PI / 2;
        centerLine.position.y = 0.05; // Slightly above asphalt
        centerLine.renderOrder = 999; // Render LAST
        dragStripGroup.add(centerLine);
        
        // White dashed lane markings - FIXED HEIGHTS
        const dashLength = 10;
        const dashGap = 5;
        const dashWidth = 0.25;
        const laneOffset = stripWidth / 3;
        
        for (let z = -stripLength/2 + 5; z < stripLength/2 - 5; z += dashLength + dashGap) {
          // Left lane marking
          const leftDashGeometry = new THREE.PlaneGeometry(dashWidth, dashLength);
          const leftDashMaterial = new THREE.MeshBasicMaterial({
            color: 0xFFFFFF,
            depthWrite: false,
            side: THREE.FrontSide
          });
          const leftDash = new THREE.Mesh(leftDashGeometry, leftDashMaterial);
          leftDash.rotation.x = -Math.PI / 2;
          leftDash.position.set(-laneOffset, 0.05, z + dashLength/2); // Same as center line
          leftDash.renderOrder = 999;
          dragStripGroup.add(leftDash);
          
          // Right lane marking
          const rightDash = leftDash.clone();
          rightDash.position.set(laneOffset, 0.05, z + dashLength/2);
          rightDash.renderOrder = 999;
          dragStripGroup.add(rightDash);
        }
        
        // White start line
        const startLineGeometry = new THREE.PlaneGeometry(stripWidth, 1.2);
        const startLineMaterial = new THREE.MeshBasicMaterial({
          color: 0xFFFFFF,
          depthWrite: false,
          side: THREE.FrontSide
        });
        const startLine = new THREE.Mesh(startLineGeometry, startLineMaterial);
        startLine.rotation.x = -Math.PI / 2;
        startLine.position.set(0, 0.05, -stripLength/2 + 0.6);
        startLine.renderOrder = 999;
        dragStripGroup.add(startLine);
        
        // Checkered finish line
        const finishLineWidth = stripWidth;
        const finishLineDepth = 2.5;
        const checkerSize = 0.6;
        
        for (let x = -finishLineWidth/2; x < finishLineWidth/2; x += checkerSize) {
          for (let z = -finishLineDepth/2; z < finishLineDepth/2; z += checkerSize) {
            const checkerGeometry = new THREE.PlaneGeometry(checkerSize, checkerSize);
            const isBlack = Math.floor((x + finishLineWidth/2) / checkerSize + (z + finishLineDepth/2) / checkerSize) % 2 === 0;
            const checkerMaterial = new THREE.MeshBasicMaterial({
              color: isBlack ? 0x000000 : 0xFFFFFF,
              depthWrite: false,
              side: THREE.FrontSide
            });
            const checker = new THREE.Mesh(checkerGeometry, checkerMaterial);
            checker.rotation.x = -Math.PI / 2;
            checker.position.set(
              x + checkerSize/2,
              0.05,
              stripLength/2 - finishLineDepth/2 + z + checkerSize/2
            );
            checker.renderOrder = 999;
            dragStripGroup.add(checker);
          }
        }
        
        // Starting blocks
        const createStartingBlock = (xOffset) => {
          const blockGeometry = new THREE.BoxGeometry(0.5, 0.3, 1);
          const blockMaterial = new THREE.MeshStandardMaterial({
            color: 0xFF0000,
            roughness: 0.5,
            metalness: 0.3
          });
          const block = new THREE.Mesh(blockGeometry, blockMaterial);
          block.position.set(xOffset, 0.15, -stripLength/2 + 3);
          block.castShadow = true;
          dragStripGroup.add(block);
        };
        
        createStartingBlock(-stripWidth/4);
        createStartingBlock(stripWidth/4);
        
        // Add to scene
        scene.add(dragStripGroup);
        console.log('Drag strip created at:', stripX, stripZ);
        
        return dragStripGroup;
      }

      function createTerrain() {
        const THREE = window.THREE;
        const terrainWidth = 2000;
        const terrainHeight = 2000;
        const terrainSegments = 128;

        const geometry = new THREE.PlaneGeometry(terrainWidth, terrainHeight, terrainSegments, terrainSegments);

        terrainHeightData = {
          width: terrainWidth,
          height: terrainHeight,
          segments: terrainSegments,
          heights: new Float32Array((terrainSegments + 1) * (terrainSegments + 1))
        };

        const vertices = geometry.attributes.position.array;
        let heightIndex = 0;
        
        // UPDATED: Match your drag strip location (-100, -600)
        const stripX = -100;  // Changed from 0
        const stripZ = -600;  // Changed from -500
        const stripLength = 500;
        const stripWidth = 40; // WIDER than actual strip (25 + buffer)
        const stripBuffer = 15;
        
        for (let i = 0; i < vertices.length; i += 3) {
          const x = vertices[i];
          const y = vertices[i + 1];
          
          const distanceFromOrigin = Math.sqrt(x * x + y * y);
          let height;
          
          // Check if this vertex is within the drag strip area
          const isInDragStripArea =
            Math.abs(x - stripX) <= (stripWidth/2 + stripBuffer) && // Account for stripX offset
            y >= (stripZ - stripLength/2 - stripBuffer) &&
            y <= (stripZ + stripLength/2 + stripBuffer);
          
          if (distanceFromOrigin < 50) {
            // Flat area for helipad
            height = -0.5;
          } else if (isInDragStripArea) {
            // FLAT area for drag strip - LOWER than water
            height = -2.5; // LOWER than water (-2) to prevent flickering
          } else {
            // Normal terrain for everywhere else
            height = 20 * (
              Math.sin(x * 0.001) * Math.cos(y * 0.0015) +
              0.3 * Math.sin(x * 0.003 + y * 0.003) +
              0.1 * Math.sin(x * 0.006 + y * 0.005)
            );
          }
          
          vertices[i + 2] = height;
          terrainHeightData.heights[heightIndex++] = height;
        }
        
        geometry.computeVertexNormals();

        const textureLoader = new THREE.TextureLoader();
        
        const grassTexture = textureLoader.load('/grass.jpg',
          () => {
            console.log('Grass texture loaded successfully');
          },
          undefined,
          (error) => {
            console.error('Error loading grass texture:', error);
          }
        );
        
        grassTexture.wrapS = THREE.RepeatWrapping;
        grassTexture.wrapT = THREE.RepeatWrapping;
        grassTexture.repeat.set(100, 100);
        grassTexture.anisotropy = renderer.capabilities.getMaxAnisotropy();

        const material = new THREE.MeshStandardMaterial({
          map: grassTexture,
          color: 0x45B049,
          roughness: 0.85,
          metalness: 0.0,
          side: THREE.FrontSide // Changed from DoubleSide
        });

        terrainMesh = new THREE.Mesh(geometry, material);
        terrainMesh.rotation.x = -Math.PI / 2;
        terrainMesh.receiveShadow = true;
        scene.add(terrainMesh);
        
        console.log('Terrain created with flattened drag strip area at:', stripX, stripZ);
      }

    function createLandingPad() {
      const THREE = window.THREE;
      
      const padRadius = 20;
      const padGeometry = new THREE.CircleGeometry(padRadius, 32);
      const padMaterial = new THREE.MeshStandardMaterial({
        color: 0x444444,
        roughness: 1.0,
        metalness: 0.4,
        side: THREE.DoubleSide
      });
      
      landingPadMesh = new THREE.Mesh(padGeometry, padMaterial);
      landingPadMesh.rotation.x = -Math.PI / 2;
      landingPadMesh.position.y = 0.8;
      landingPadMesh.receiveShadow = true;
      scene.add(landingPadMesh);
      
      const markingMaterial = new THREE.MeshBasicMaterial({ color: 0xFFFFFF });
      const HGeometry = new THREE.PlaneGeometry(12, 2);
      const HVertical = new THREE.Mesh(HGeometry, markingMaterial);
      HVertical.rotation.x = -Math.PI / 2;
      HVertical.position.set(0, 1.0, 0);
      scene.add(HVertical);
      
      const HHorizontal = new THREE.PlaneGeometry(2, 8);
      const HHoriz = new THREE.Mesh(HHorizontal, markingMaterial);
      HHoriz.rotation.x = -Math.PI / 2;
      HHoriz.position.set(0, 1.0, -3);
      scene.add(HHoriz);
      
      const borderGeometry = new THREE.RingGeometry(padRadius - 1, padRadius, 32);
      const borderMaterial = new THREE.MeshBasicMaterial({
        color: 0xFFFF00,
        side: THREE.DoubleSide
      });
      const border = new THREE.Mesh(borderGeometry, borderMaterial);
      border.rotation.x = -Math.PI / 2;
      border.position.y = 0.85;
      scene.add(border);
    }

    function getTerrainHeight(x, z) {
      if (!terrainHeightData) return 0;
      
      const data = terrainHeightData;
      const normalizedX = (x / data.width) + 0.5;
      const normalizedZ = (z / data.height) + 0.5;
      
      const gridX = Math.floor(normalizedX * data.segments);
      const gridZ = Math.floor(normalizedZ * data.segments);
      
      const clampedX = Math.max(0, Math.min(data.segments, gridX));
      const clampedZ = Math.max(0, Math.min(data.segments, gridZ));
      
      const index = clampedZ * (data.segments + 1) + clampedX;
      return data.heights[index] || 0;
    }

    function isOnLandingPad(x, z) {
      const distanceFromOrigin = Math.sqrt(x * x + z * z);
      return distanceFromOrigin < 20;
    }

      function createWater() {
        const THREE = window.THREE;
        // Make water plane SMALLER and position it differently
        const waterGeometry = new THREE.PlaneGeometry(1200, 1200);
        const waterMaterial = new THREE.MeshStandardMaterial({
          color: 0x0B0B60,
          transparent: true,
          opacity: 0.7,
          roughness: 0.8,
          metalness: 0.6,
          side: THREE.FrontSide // Changed from DoubleSide
        });

        waterMesh = new THREE.Mesh(waterGeometry, waterMaterial);
        waterMesh.rotation.x = -Math.PI / 2;
        waterMesh.position.set(200, -1, 200); // Position AWAY from drag strip
        scene.add(waterMesh);
        console.log('Water positioned away from drag strip');
      }
      
    function createTree(x, z) {
      const THREE = window.THREE;
      const treeGroup = new THREE.Group();

      const trunkGeometry = new THREE.CylinderGeometry(1.5, 2, 15, 8);
      const trunkMaterial = new THREE.MeshStandardMaterial({ color: 0x8B4513, roughness: 0.9 });
      const trunk = new THREE.Mesh(trunkGeometry, trunkMaterial);
      trunk.castShadow = true;
      trunk.position.y = 7.5;
      treeGroup.add(trunk);

      const foliageGeometry = new THREE.ConeGeometry(8, 20, 8);
      const foliageMaterial = new THREE.MeshStandardMaterial({ color: 0x228B22, roughness: 0.9 });
      const foliage = new THREE.Mesh(foliageGeometry, foliageMaterial);
      foliage.castShadow = true;
      foliage.position.y = 22;
      treeGroup.add(foliage);

      const terrainHeight = getTerrainHeight(x, z);
      treeGroup.position.set(x, terrainHeight, z);
      treeGroup.rotation.y = Math.random() * Math.PI * 2;
      const scale = 0.8 + Math.random() * 0.4;
      treeGroup.scale.set(scale, scale, scale);

      if (!isOnLandingPad(x, z)) {
        scene.add(treeGroup);
        trees.push(treeGroup);
      }
    }

    function createRock(x, z) {
      const THREE = window.THREE;
      const rockGroup = new THREE.Group();

      // Create irregular rock shape using multiple spheres
      const rockColors = [0x505050, 0x606060, 0x707070, 0x454545];
      const rockColor = rockColors[Math.floor(Math.random() * rockColors.length)];
      
      const rockMaterial = new THREE.MeshStandardMaterial({
        color: rockColor,
        roughness: 0.95,
        metalness: 0.1
      });

      // Main rock body - flattened sphere
      const mainGeometry = new THREE.SphereGeometry(1, 8, 8);
      const mainRock = new THREE.Mesh(mainGeometry, rockMaterial);
      mainRock.castShadow = true;
      mainRock.receiveShadow = true;
      mainRock.scale.set(1, 0.6, 1); // Flatten vertically
      mainRock.position.y = 0;
      rockGroup.add(mainRock);

      // Add 2-4 smaller bumps for irregular shape
      const bumpCount = 2 + Math.floor(Math.random() * 3);
      for (let i = 0; i < bumpCount; i++) {
        const bumpGeometry = new THREE.SphereGeometry(0.5, 6, 6);
        const bump = new THREE.Mesh(bumpGeometry, rockMaterial);
        bump.position.set(
          (Math.random() - 0.5) * 1.5,
          Math.random() * 0.5,
          (Math.random() - 0.5) * 1.5
        );
        const bumpScale = 0.5 + Math.random() * 0.8;
        bump.scale.set(bumpScale, bumpScale * 0.6, bumpScale);
        bump.castShadow = true;
        rockGroup.add(bump);
      }

      const terrainHeight = getTerrainHeight(x, z);
      rockGroup.position.set(x, terrainHeight - 0.3, z); // Partially submerged
      rockGroup.rotation.y = Math.random() * Math.PI * 2;
      rockGroup.rotation.x = (Math.random() - 0.5) * 0.3;
      rockGroup.rotation.z = (Math.random() - 0.5) * 0.3;
      
      const scale = 3 + Math.random() * 4; // Larger rocks
      rockGroup.scale.set(scale, scale, scale);

      scene.add(rockGroup);
      trees.push(rockGroup); // Reuse trees array for consistency
    }

      function createTreesAndRocks(count) {
        for (let i = 0; i < count; i++) {
          const angle = Math.random() * Math.PI * 2;
          const radius = 50 + Math.random() * 600;
          const x = Math.cos(angle) * radius;
          const z = Math.sin(angle) * radius;
          
          // Check for drag strip at new location
          const isOnDragStrip =
            z > -850 && z < -350 && Math.abs(x + 100) < 20; // Adjust for -100 offset
          
          if (!isOnLandingPad(x, z) && !isOnDragStrip) {
            const terrainHeight = getTerrainHeight(x, z);
            
            // If below water level (-1), create rock instead of tree
            if (terrainHeight < -1) {
              createRock(x, z);
            } else {
              createTree(x, z);
            }
          }
        }
      }

    function createCloud(x, y, z) {
      const THREE = window.THREE;
      const cloudGroup = new THREE.Group();
      const cloudColors = [0xFFFFFF, 0xFFE4F0, 0xFFF0E6];
      const cloudColor = cloudColors[Math.floor(Math.random() * cloudColors.length)];

      const cloudGeometry = new THREE.SphereGeometry(1, 8, 8);
      const cloudMaterial = new THREE.MeshStandardMaterial({
        color: cloudColor,
        transparent: true,
        opacity: 0.5,
        roughness: 1
      });

      const puffCount = 4 + Math.floor(Math.random() * 4);
      
      for (let i = 0; i < puffCount; i++) {
        const puff = new THREE.Mesh(cloudGeometry, cloudMaterial);
        puff.position.set(
          (Math.random() - 0.5) * 25,
          (Math.random() - 0.5) * 10,
          (Math.random() - 0.5) * 25
        );
        const size = 8 + Math.random() * 10;
        puff.scale.set(size, size * 0.5, size);
        cloudGroup.add(puff);
      }

      cloudGroup.position.set(x, y, z);
      const scale = 1 + Math.random() * 1;
      cloudGroup.scale.set(scale, scale * 0.6, scale);
      cloudGroup.speed = 0.3 + Math.random() * 0.4;

      scene.add(cloudGroup);
      clouds.push(cloudGroup);
    }

    function createClouds(count) {
      for (let i = 0; i < count; i++) {
        const angle = Math.random() * Math.PI * 2;
        const radius = 200 + Math.random() * 800;
        const x = Math.cos(angle) * radius;
        const z = Math.sin(angle) * radius;
        const y = 150 + (Math.random() - 0.5) * 120;
        createCloud(x, y, z);
      }
    }

      function loadHelicopterModel() {
        const THREE = window.THREE;
        
        if (!window.THREE || !window.THREE.GLTFLoader) {
          console.warn('Three.js not fully loaded');
          return;
        }

        try {
          const loader = new THREE.GLTFLoader();
          
          loader.load(
            '/helicopter.glb',
            (gltf) => {
              if (!scene) {
                console.log('Scene no longer exists, skipping helicopter load');
                return;
              }
              
              helicopterModel = gltf.scene;
              
              helicopterModel.scale.set(2.4, 2.4, 2.4);
              helicopterModel.rotation.y = helicopterRotation;
              
              if (gltf.animations && gltf.animations.length > 0) {
                helicopterMixer = new THREE.AnimationMixer(helicopterModel);
                gltf.animations.forEach((clip) => {
                  const action = helicopterMixer.clipAction(clip);
                  action.play();
                });
              }
              
              helicopterModel.traverse((child) => {
                if (child.isMesh) {
                  child.castShadow = true;
                  child.receiveShadow = true;
                  
                  if (child.material) {
                    child.material.envMapIntensity = 0.5;
                    child.material.needsUpdate = true;
                    
                    if (child.material.isMeshStandardMaterial) {
                      child.material.roughness = Math.max(0.1, child.material.roughness || 0.3);
                      child.material.metalness = Math.min(0.8, child.material.metalness || 0.5);
                      child.material.emissive = new THREE.Color(0x222222);
                      child.material.emissiveIntensity = 0.05;
                    }
                    
                    if (child.material.color) {
                      const currentColor = child.material.color.clone();
                      currentColor.multiplyScalar(1.2);
                      child.material.color = currentColor;
                    }
                  }
                }
              });
              
              helicopterModel.position.y = helicopterAltitude;
              helicopterModel.visible = (mode === 'helicopter');
              scene.add(helicopterModel);
            },
            (progress) => {
              console.log(`Loading helicopter model: ${(progress.loaded / progress.total * 100).toFixed(2)}%`);
            },
            (error) => {
              console.error('Error loading helicopter model:', error);
            }
          );
        } catch (error) {
          console.error('Failed to create GLTFLoader:', error);
        }
      }

      function loadCarModel() {
        const THREE = window.THREE;
        
          const CAR_START_X = 0;
          const CAR_START_Z = -395;
        
        if (!window.THREE || !window.THREE.GLTFLoader) {
          console.warn('Three.js not fully loaded');
          return;
        }

        try {
          const loader = new THREE.GLTFLoader();
          
          loader.load(
            '/car.glb',
            (gltf) => {
              if (!scene) {
                console.log('Scene no longer exists, skipping car load');
                return;
              }
              
              carModel = gltf.scene;
              
              carModel.scale.set(2, 2, 2);
              carModel.rotation.y = carRotation;
              
              if (gltf.animations && gltf.animations.length > 0) {
                carMixer = new THREE.AnimationMixer(carModel);
                gltf.animations.forEach((clip) => {
                  const action = carMixer.clipAction(clip);
                  action.play();
                });
              }
              
              carModel.traverse((child) => {
                if (child.isMesh) {
                  child.castShadow = true;
                  child.receiveShadow = true;
                  
                  if (child.material) {
                    if (child.material.map) {
                      child.material.map.encoding = THREE.sRGBEncoding;
                    }
                    
                    child.material.envMapIntensity = 0.3;
                    child.material.needsUpdate = true;
                    
                    if (child.material.isMeshStandardMaterial) {
                      child.material.roughness = Math.max(0.2, child.material.roughness || 0.5);
                      child.material.metalness = Math.min(1.0, child.material.metalness || 0.3);
                    }
                  }
                }
              });
              
              carHeight = getTerrainHeight(CAR_START_X, CAR_START_Z) + 1.5;
              carModel.position.set(CAR_START_X, carHeight, CAR_START_Z);
              carModel.visible = (mode === 'car');
              scene.add(carModel);
            },
            (progress) => {
              console.log(`Loading car model: ${(progress.loaded / progress.total * 100).toFixed(2)}%`);
            },
            (error) => {
              console.error('Error loading car model:', error);
            }
          );
        } catch (error) {
          console.error('Failed to create GLTFLoader for car:', error);
        }
      }
    
      function loadAvatarModel() {
        const THREE = window.THREE;
        
        if (!window.THREE || !window.THREE.GLTFLoader) {
          console.warn('Three.js not fully loaded');
          return;
        }

        try {
          const loader = new THREE.GLTFLoader();
          
          loader.load(
            './me.glb',
            (gltf) => {
              if (!scene) {
                console.log('Scene no longer exists, skipping avatar load');
                return;
              }
              
              avatarModel = gltf.scene;
              
              avatarModel.scale.set(6.5, 6.0, 6.5);
              avatarModel.rotation.y = avatarRotation;
              
              // Store animations
              avatarAnimations = gltf.animations || [];
              
              if (gltf.animations && gltf.animations.length > 0) {
                avatarMixer = new THREE.AnimationMixer(avatarModel);
                
                // ALWAYS play the first animation continuously
                if (gltf.animations[0]) {
                  currentAvatarAction = avatarMixer.clipAction(gltf.animations[0]);
                  currentAvatarAction.play();
                  currentAvatarAction.setEffectiveTimeScale(1.0); // Normal speed
                  console.log('Playing default animation:', gltf.animations[0].name);
                }
              }
              
              avatarModel.traverse((child) => {
                if (child.isMesh) {
                  child.castShadow = true;
                  child.receiveShadow = true;
                  
                  if (child.material) {
                    child.material.envMapIntensity = 0.5;
                    child.material.needsUpdate = true;
                    
                    if (child.material.isMeshStandardMaterial) {
                      child.material.roughness = Math.max(0.1, child.material.roughness || 0.3);
                      child.material.metalness = Math.min(0.8, child.material.metalness || 0.5);
                    }
                  }
                }
              });
              
              const terrainHeight = getTerrainHeight(10, 20);
              avatarModel.position.set(10, terrainHeight + AVATAR_HEIGHT, 10);
              avatarModel.visible = (mode === 'avatar');
              scene.add(avatarModel);
              
              console.log('Avatar loaded with', avatarAnimations.length, 'animations');
            },
            (progress) => {
              console.log(`Loading avatar model: ${(progress.loaded / progress.total * 100).toFixed(2)}%`);
            },
            (error) => {
              console.error('Error loading avatar model:', error);
            }
          );
        } catch (error) {
          console.error('Failed to create GLTFLoader for avatar:', error);
        }
      }
      function setupOrbitControls() {
        const THREE = window.THREE;
        if (!controls && THREE.OrbitControls) {
          controls = new THREE.OrbitControls(camera, renderer.domElement);
          controls.enableDamping = true;
          controls.dampingFactor = 0.05;
          controls.screenSpacePanning = true;
          controls.maxPolarAngle = Math.PI * 0.95;
          controls.minDistance = 1;
          controls.maxDistance = 2000;
          controls.mouseButtons = {
            LEFT: THREE.MOUSE.ROTATE,
            MIDDLE: THREE.MOUSE.DOLLY,
            RIGHT: THREE.MOUSE.PAN
          };
          controls.touches = {
            ONE: THREE.TOUCH.ROTATE,
            TWO: THREE.TOUCH.DOLLY_PAN
          };
          // Start with controls enabled
          controls.enabled = true;
        }
      }

      function updateOrbitControlsState() {
        if (!controls) return;
        
        // Orbit controls should work in BOTH view modes for avatar
        // Only disable when user is actively controlling the avatar in vehicle view
        const movementKeys = ['w', 's', 'a', 'd', 'arrowup', 'arrowdown', 'arrowleft', 'arrowright'];
        const isMoving = movementKeys.some(key => keys[key]);
        
        if (mode === 'avatar' && viewMode === 'vehicle' && isMoving) {
          // When controlling avatar in vehicle view, disable orbit controls
          controls.enabled = false;
          setIsControllingVehicle(true);
        } else {
          // In all other cases, enable orbit controls
          controls.enabled = true;
          setIsControllingVehicle(isMoving && mode !== 'avatar');
        }
        
        // Update controls target based on current mode
        if (mode === 'helicopter' && helicopterModel) {
          controls.target.copy(helicopterModel.position);
        } else if (mode === 'car' && carModel) {
          controls.target.copy(carModel.position);
        } else if (mode === 'avatar' && avatarModel) {
          controls.target.copy(avatarModel.position);
        }
        
        controls.update();
      }

      function setupControls() {
        document.addEventListener('keydown', (e) => {
          keys[e.key.toLowerCase()] = true;
          
          if (e.key.toLowerCase() === 'r' && mode === 'helicopter') {
            isRotorOn = !isRotorOn;
            setRotorAnimation(isRotorOn);
          }
          
          if (e.key.toLowerCase() === 'c') {
            e.preventDefault();
            // Debounce the state change
            setTimeout(() => {
              setViewMode(prev => prev === 'vehicle' ? 'free' : 'vehicle');
            }, 50);
          }
          
          if (e.key === '1') {
            setTimeout(() => {
              setMode('helicopter');
            }, 50);
          }
          if (e.key === '2') {
            setTimeout(() => {
              setMode('car');
            }, 50);
          }
          if (e.key === '3') {
            setTimeout(() => {
              setMode('avatar');
            }, 50);
          }
          
          if (e.key === ' ') {
            setTimeout(() => {
              setIsPaused(p => !p);
            }, 50);
          }
          
          if (e.key.toLowerCase() === 'control' && mode === 'avatar') {
            isAvatarCrouching = true;
          }
          
          if (e.key.toLowerCase() === ' ' && mode === 'avatar' && !isAvatarJumping) {
            isAvatarJumping = true;
            avatarJumpVelocity = JUMP_FORCE;
          }
        });

        document.addEventListener('keyup', (e) => {
          keys[e.key.toLowerCase()] = false;
          
          if (e.key.toLowerCase() === 'control' && mode === 'avatar') {
            isAvatarCrouching = false;
          }
        });
      }
      
      
    function updateHelicopterMovement(delta) {
      if (!helicopterModel || mode !== 'helicopter') return;

      const maxVerticalSpeed = 2.0;
      const maxHorizontalSpeed = 1.5;
      const verticalAcceleration = 0.05;
      const horizontalAcceleration = 0.03;
      const turnSpeed = 0.02;
      const tiltSpeed = 0.01;
      const rotorAcceleration = 0.1;
      const maxRotorSpeed = 10;
      const PAD_HEIGHT = 0.8;
      const HELI_HEIGHT_ABOVE_PAD = 2.5;

      if (isRotorOn) {
        rotorSpeed = Math.min(rotorSpeed + rotorAcceleration * 0.5, maxRotorSpeed);
      } else {
        rotorSpeed = Math.max(rotorSpeed - rotorAcceleration * 0.5, 0);
      }

      if (keys['w'] || keys['arrowup']) {
        if (rotorSpeed > 3) {
          targetVerticalSpeed = maxVerticalSpeed * (rotorSpeed / maxRotorSpeed);
        }
      } else if (keys['s'] || keys['arrowdown']) {
        targetVerticalSpeed = -maxVerticalSpeed * 0.5;
      } else {
        targetVerticalSpeed = 0;
      }

      if (currentVerticalSpeed < targetVerticalSpeed) {
        currentVerticalSpeed = Math.min(currentVerticalSpeed + verticalAcceleration, targetVerticalSpeed);
      } else if (currentVerticalSpeed > targetVerticalSpeed) {
        currentVerticalSpeed = Math.max(currentVerticalSpeed - verticalAcceleration, targetVerticalSpeed);
      }

      if ((keys['a'] || keys['arrowleft']) && isRotorOn) {
        targetHorizontalSpeed = maxHorizontalSpeed;
        roll = Math.max(roll - tiltSpeed, -0.3);
      } else if ((keys['d'] || keys['arrowright']) && isRotorOn) {
        targetHorizontalSpeed = -maxHorizontalSpeed;
        roll = Math.min(roll + tiltSpeed, 0.3);
      } else {
        targetHorizontalSpeed = 0;
        roll *= 0.95;
      }

      if (keys['q'] && isRotorOn) {
        pitch = Math.max(pitch - tiltSpeed, -0.3);
        helicopterRotation += turnSpeed * 0.5;
      } else if (keys['e'] && isRotorOn) {
        pitch = Math.min(pitch + tiltSpeed, 0.3);
        helicopterRotation -= turnSpeed * 0.5;
      } else {
        pitch *= 0.95;
      }

      if (keys['z'] && isRotorOn) {
        yaw += turnSpeed;
      } else if (keys['x'] && isRotorOn) {
        yaw -= turnSpeed;
      } else {
        yaw *= 0.95;
      }

      if (currentHorizontalSpeed < targetHorizontalSpeed) {
        currentHorizontalSpeed = Math.min(currentHorizontalSpeed + horizontalAcceleration, targetHorizontalSpeed);
      } else if (currentHorizontalSpeed > targetHorizontalSpeed) {
        currentHorizontalSpeed = Math.max(currentHorizontalSpeed - horizontalAcceleration, targetHorizontalSpeed);
      }

      if ((isAirborne || (currentVerticalSpeed > 0 && isRotorOn && rotorSpeed > 3))) {
        helicopterAltitude += currentVerticalSpeed;
        helicopterModel.position.y = helicopterAltitude;
      }

      if (Math.abs(currentHorizontalSpeed) > 0.01 && isAirborne && isRotorOn) {
        helicopterModel.position.x -= Math.sin(helicopterRotation) * currentHorizontalSpeed;
        helicopterModel.position.z -= Math.cos(helicopterRotation) * currentHorizontalSpeed;
      }

      if (Math.abs(pitch) > 0.01 && isAirborne && isRotorOn) {
        const forwardSpeed = Math.abs(pitch) * 0.5;
        helicopterModel.position.x -= Math.sin(helicopterRotation) * forwardSpeed;
        helicopterModel.position.z -= Math.cos(helicopterRotation) * forwardSpeed;
      }

      helicopterModel.rotation.y = helicopterRotation + yaw;
      helicopterModel.rotation.x = pitch;
      helicopterModel.rotation.z = roll;

      if (helicopterMixer) {
        helicopterMixer.update(delta * (rotorSpeed / maxRotorSpeed));
      } else {
        helicopterModel.traverse((child) => {
          if (child.name === 'mainRotor') {
            child.rotation.y += delta * rotorSpeed * 10;
          }
          if (child.name === 'tailRotor') {
            child.rotation.z += delta * rotorSpeed * 15;
          }
        });
      }

      const terrainHeight = getTerrainHeight(helicopterModel.position.x, helicopterModel.position.z);
      const heightAboveTerrain = helicopterAltitude - terrainHeight;
      const heightAbovePad = helicopterAltitude - PAD_HEIGHT;
      
      setAltitude(Math.round(heightAboveTerrain));
      
      const onPad = isOnLandingPad(helicopterModel.position.x, helicopterModel.position.z);
      
      if (!isAirborne) {
        setIsOnGround(true);
        
        if (isRotorOn && rotorSpeed > 5 && currentVerticalSpeed > 0.5 && onPad) {
          isAirborne = true;
          setIsOnGround(false);
        }
        
        helicopterModel.position.y = PAD_HEIGHT + HELI_HEIGHT_ABOVE_PAD;
        helicopterAltitude = helicopterModel.position.y;
      } else {
        setIsOnGround(false);
        
        if (currentVerticalSpeed < 0) {
          helicopterAltitude += currentVerticalSpeed * 0.5;
        }
        
        if (heightAbovePad < 3 && rotorSpeed < 3 && Math.abs(currentVerticalSpeed) < 0.2 && onPad) {
          isAirborne = false;
          setIsOnGround(true);
          helicopterModel.position.y = PAD_HEIGHT + HELI_HEIGHT_ABOVE_PAD;
          helicopterAltitude = helicopterModel.position.y;
          currentVerticalSpeed = 0;
          pitch = 0;
          roll = 0;
          yaw = 0;
        }
        
        if (heightAboveTerrain < 1) {
          isAirborne = false;
          setIsOnGround(true);
          helicopterModel.position.y = terrainHeight + 2;
          helicopterAltitude = helicopterModel.position.y;
          currentVerticalSpeed = 0;
          rotorSpeed = 0;
          isRotorOn = false;
          setRotorAnimation(false);
        }
      }
      
      const totalSpeed = Math.sqrt(
        currentVerticalSpeed * currentVerticalSpeed +
        currentHorizontalSpeed * currentHorizontalSpeed
      );
      setSpeed(Math.abs(totalSpeed * 50).toFixed(0));
    }

      function updateCarMovement(delta) {
        if (!carModel || mode !== 'car') return;

        const maxSpeed = 1.2;
        const acceleration = 0.05;
        const deceleration = 0.05;
        const turnSpeed = 0.03;
        const boostMultiplier = keys['shift'] ? 1.8 : 1;

        if (keys['w'] || keys['arrowup']) {
          targetCarSpeed = maxSpeed * boostMultiplier;
        } else if (keys['s'] || keys['arrowdown']) {
          targetCarSpeed = -maxSpeed * 0.6;
        } else {
          targetCarSpeed = 0;
        }

        if (currentCarSpeed < targetCarSpeed) {
          currentCarSpeed = Math.min(currentCarSpeed + acceleration, targetCarSpeed);
        } else if (currentCarSpeed > targetCarSpeed) {
          currentCarSpeed = Math.max(currentCarSpeed - deceleration, targetCarSpeed);
        }

        if (Math.abs(currentCarSpeed) > 0.01) {
          carModel.position.x -= Math.sin(carRotation) * currentCarSpeed;
          carModel.position.z -= Math.cos(carRotation) * currentCarSpeed;
        }

        if (Math.abs(currentCarSpeed) > 0.1) {
          if (keys['a'] || keys['arrowleft']) {
            carRotation += turnSpeed * Math.sign(currentCarSpeed);
          }
          if (keys['d'] || keys['arrowright']) {
            carRotation -= turnSpeed * Math.sign(currentCarSpeed);
          }
        }

        carModel.rotation.y = carRotation;

        const terrainHeight = getTerrainHeight(carModel.position.x, carModel.position.z);
        
        // Check if car is on the drag strip area
        const isOnDragStrip =
          carModel.position.x >= -112.5 && carModel.position.x <= -87.5 && // stripWidth 25, so ±12.5
          carModel.position.z >= -850 && carModel.position.z <= -350; // stripLength 500, centered at -600
        
        // If on drag strip, use higher height; otherwise use normal height
        const targetHeight = isOnDragStrip ? terrainHeight + 2.5 : terrainHeight + 1.5;
        
        const heightDiff = targetHeight - carHeight;
        
        if (Math.abs(heightDiff) > 0.01) {
          carHeight += heightDiff * 0.15;
        }
        
        carModel.position.y = carHeight;

        if (carMixer) {
          carMixer.update(delta);
        }

        setSpeed(Math.abs(currentCarSpeed * 50).toFixed(0));
        setAltitude(Math.round(carHeight - terrainHeight));
      }

      function updateAvatarMovement(delta) {
        if (!avatarModel || mode !== 'avatar') return;

        const maxSpeed = 0.5;
        const acceleration = 0.1;
        const deceleration = 0.15;
        const turnSpeed = 0.03;
        const runMultiplier = keys['shift'] ? 2.0 : 1.0;
        const crouchMultiplier = 0.5;

        let movementSpeedMultiplier = 1.0;
        if (isAvatarCrouching) {
          movementSpeedMultiplier = crouchMultiplier;
        }

        if (keys['w'] || keys['arrowup']) {
          targetAvatarSpeed = maxSpeed * movementSpeedMultiplier * runMultiplier;
        } else if (keys['s'] || keys['arrowdown']) {
          targetAvatarSpeed = -maxSpeed * 0.7 * movementSpeedMultiplier;
        } else {
          targetAvatarSpeed = 0;
        }

        if (currentAvatarSpeed < targetAvatarSpeed) {
          currentAvatarSpeed = Math.min(currentAvatarSpeed + acceleration, targetAvatarSpeed);
        } else if (currentAvatarSpeed > targetAvatarSpeed) {
          currentAvatarSpeed = Math.max(currentAvatarSpeed - deceleration, targetAvatarSpeed);
        }

        if (Math.abs(currentAvatarSpeed) > 0.01) {
          avatarModel.position.x -= Math.sin(avatarRotation) * currentAvatarSpeed;
          avatarModel.position.z -= Math.cos(avatarRotation) * currentAvatarSpeed;
        }

        if (keys['a'] || keys['arrowleft']) {
          avatarRotation += turnSpeed;
        }
        if (keys['d'] || keys['arrowright']) {
          avatarRotation -= turnSpeed;
        }

        avatarModel.rotation.y = avatarRotation;

        const terrainHeight = getTerrainHeight(avatarModel.position.x, avatarModel.position.z);
        const currentHeight = isAvatarCrouching ? AVATAR_CROUCH_HEIGHT : AVATAR_HEIGHT;
        
        if (isAvatarJumping) {
          avatarJumpVelocity += GRAVITY;
          avatarModel.position.y += avatarJumpVelocity;
          
          if (avatarModel.position.y <= terrainHeight + currentHeight) {
            avatarModel.position.y = terrainHeight + currentHeight;
            avatarJumpVelocity = 0;
            isAvatarJumping = false;
          }
        } else {
          avatarModel.position.y = terrainHeight + currentHeight;
        }

        // SIMPLIFIED: Just update the mixer - animation plays continuously
        if (avatarMixer) {
          avatarMixer.update(delta);
        }

        setSpeed(Math.abs(currentAvatarSpeed * 10).toFixed(0));
        setAltitude(Math.round(avatarModel.position.y - terrainHeight));
      }

    function updateCamera() {
      if (!camera) return;

      if (viewMode === 'vehicle') {
        let cameraDistance, cameraHeight;
        let targetModel, targetRotation;
        
        if (mode === 'helicopter' && helicopterModel) {
          targetModel = helicopterModel;
          targetRotation = helicopterRotation;
          
          if (isAirborne) {
            cameraDistance = 15;
            cameraHeight = 8;
          } else {
            cameraDistance = 10;
            cameraHeight = 5;
          }
        } else if (mode === 'car' && carModel) {
          targetModel = carModel;
          targetRotation = carRotation;
          cameraDistance = 30;
          cameraHeight = 15;
        } else if (mode === 'avatar' && avatarModel) {
          // First-person camera for avatar
          const offset = new window.THREE.Vector3(
            Math.sin(avatarRotation) * 0.2,
            isAvatarCrouching ? 0.8 : 1.5,
            Math.cos(avatarRotation) * 0.2
          );
          
          camera.position.copy(avatarModel.position).add(offset);
          
          const lookAt = new window.THREE.Vector3(
            avatarModel.position.x - Math.sin(avatarRotation) * 10,
            avatarModel.position.y + (isAvatarCrouching ? 0.8 : 1.5),
            avatarModel.position.z - Math.cos(avatarRotation) * 10
          );
          
          camera.lookAt(lookAt);
          return;
        } else {
          return;
        }
        
        const movementKeys = ['w', 's', 'a', 'd', 'q', 'e', 'z', 'x', 'arrowup', 'arrowdown', 'arrowleft', 'arrowright', 'shift'];
        const isMoving = movementKeys.some(key => keys[key]);
        
        if (isMoving) {
          const cameraX = targetModel.position.x + Math.sin(targetRotation) * cameraDistance;
          const cameraZ = targetModel.position.z + Math.cos(targetRotation) * cameraDistance;
          const cameraY = targetModel.position.y + cameraHeight;
          
          camera.position.lerp(new window.THREE.Vector3(cameraX, cameraY, cameraZ), 0.1);
          
          const lookAt = new window.THREE.Vector3(
            targetModel.position.x - Math.sin(targetRotation) * (mode === 'car' ? 10 : 0),
            targetModel.position.y + 2,
            targetModel.position.z - Math.cos(targetRotation) * (mode === 'car' ? 10 : 0)
          );
          
          camera.lookAt(lookAt);
        }
      }
    }

    function updateClouds() {
      clouds.forEach((cloud, i) => {
        cloud.position.x += 0.05;
        
        const time = Date.now() * 0.001;
        cloud.position.y += Math.sin(time * 0.5 + i) * 0.02;
        
        if (cloud.position.x > 1200) {
          cloud.position.x = -1200;
          cloud.position.z = (Math.random() - 0.5) * 1200;
          cloud.position.y = 150 + Math.random() * 120;
        }
      });
    }

    function animate() {
      animationId = requestAnimationFrame(animate);
      const delta = clock ? clock.getDelta() : 0.016;

      if (!isPaused) {
        if (mode === 'helicopter') {
          updateHelicopterMovement(delta);
        } else if (mode === 'car') {
          updateCarMovement(delta);
        } else if (mode === 'avatar') {
          updateAvatarMovement(delta);
        }
        
        updateOrbitControlsState();
        updateCamera();
        updateClouds();

        if (waterMesh) {
          waterMesh.rotation.z += 0.00005;
        }

        const vehicleSpotlight = scene.getObjectByName('vehicleSpotlight');
        if (vehicleSpotlight) {
          if (mode === 'helicopter' && helicopterModel) {
            vehicleSpotlight.position.set(
              helicopterModel.position.x,
              helicopterModel.position.y + 15,
              helicopterModel.position.z
            );
          } else if (mode === 'car' && carModel) {
            vehicleSpotlight.position.set(
              carModel.position.x,
              carModel.position.y + 15,
              carModel.position.z
            );
          } else if (mode === 'avatar' && avatarModel) {
            vehicleSpotlight.position.set(
              avatarModel.position.x,
              avatarModel.position.y + 10,
              avatarModel.position.z
            );
          }
        }
      }

      if (renderer && scene && camera) {
        renderer.render(scene, camera);
      }
    }

    function onWindowResize() {
      if (camera && renderer) {
        camera.aspect = window.innerWidth / window.innerHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(window.innerWidth, window.innerHeight);
      }
    }
      initScene();

      return () => {
        if (animationId) {
          cancelAnimationFrame(animationId);
          animationId = null;
        }
        
        window.removeEventListener('resize', onWindowResize);
        
        document.body.style.overflow = '';
        document.body.style.margin = '';
        document.body.style.padding = '';
        
        // Dispose controls
        if (controls) {
          controls.dispose();
          controls = null;
        }
        
        // Stop all animations
        if (helicopterMixer) {
          helicopterMixer.stopAllAction();
          helicopterMixer = null;
        }
        
        if (carMixer) {
          carMixer.stopAllAction();
          carMixer = null;
        }
        
        if (avatarMixer) {
          avatarMixer.stopAllAction();
          avatarMixer = null;
        }
        
        // Safely remove renderer
        try {
          if (renderer && renderer.domElement && containerRef.current) {
            const domElement = renderer.domElement;
            if (domElement.parentNode === containerRef.current) {
              containerRef.current.removeChild(domElement);
            }
          }
          if (renderer) {
            renderer.dispose();
            renderer = null;
          }
        } catch (error) {
          console.warn('Error during renderer cleanup:', error);
        }
        
        // Clear references
        scene = null;
        camera = null;
        helicopterModel = null;
        carModel = null;
        avatarModel = null;
        terrainMesh = null;
        waterMesh = null;
        landingPadMesh = null;
        trees = [];
        clouds = [];
        terrainHeightData = null;
        clock = null;
        avatarAnimations = [];
        currentAvatarAction = null;
        
        // Clear keys object
        Object.keys(keys).forEach(key => delete keys[key]);
      };
    }, [isPaused, mode, viewMode]); // This closes the useEffect hook
    
    return (
      <div className="fixed inset-0 w-full h-full bg-gray-900 overflow-hidden" style={{ touchAction: 'none' }}>
        <div ref={containerRef} className="absolute inset-0 w-full h-full" />

        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-blue-500 to-purple-600">
            <div className="text-center text-white">
              <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-white mx-auto mb-4"></div>
              <p className="text-xl font-bold">Loading Adventure Simulator...</p>
            </div>
          </div>
        )}

        {!isLoading && (
          <>
            {/* Top bar - only Settings button remains */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              padding: '16px',
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.5), transparent)',
              pointerEvents: 'none'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                color: 'white',
                pointerEvents: 'auto'
              }}>
                <button
                  onClick={() => setShowSettings(!showSettings)}
                  style={{
                    background: 'rgba(0,0,0,0.6)',
                    padding: '12px',
                    borderRadius: '8px',
                    backdropFilter: 'blur(4px)',
                    border: 'none',
                    color: 'white',
                    cursor: 'pointer',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.8)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.6)'}
                >
                  <Settings size={20} />
                </button>
              </div>
            </div>

            {/* Bottom left - Stats */}
            <div style={{
              position: 'absolute',
              bottom: '16px',
              left: '16px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              pointerEvents: 'none'
            }}>
              {/* Stats container */}
              <div style={{
                display: 'flex',
                gap: '8px',
                pointerEvents: 'auto'
              }}>
                {/* Speed */}
                <div style={{
                  background: 'rgba(0,0,0,0.6)',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  backdropFilter: 'blur(8px)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  minWidth: '100px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '11px', color: '#d1d5db', marginBottom: '4px' }}>SPEED</div>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: 'white' }}>
                    {speed} <span style={{ fontSize: '12px', color: '#9ca3af' }}>km/h</span>
                  </div>
                </div>
                
                {/* Altitude */}
                <div style={{
                  background: 'rgba(0,0,0,0.6)',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  backdropFilter: 'blur(8px)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  minWidth: '100px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '11px', color: '#d1d5db', marginBottom: '4px' }}>ALTITUDE</div>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: 'white' }}>
                    {altitude} <span style={{ fontSize: '12px', color: '#9ca3af' }}>m</span>
                  </div>
                </div>
                
                {/* Status */}
                <div style={{
                  background: 'rgba(0,0,0,0.6)',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  backdropFilter: 'blur(8px)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  minWidth: '120px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '11px', color: '#d1d5db', marginBottom: '4px' }}>STATUS</div>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    color: mode === 'helicopter' ?
                      (isOnGround ? '#fbbf24' : '#60a5fa') :
                      mode === 'car' ? '#34d399' : '#ec4899'
                  }}>
                    {mode === 'helicopter' ? (isOnGround ? 'ON PAD' : 'AIRBORNE') :
                      mode === 'car' ? 'DRIVING' :
                      mode === 'avatar' ? 'WALKING' : 'UNKNOWN'}
                  </div>
                </div>
                
                {/* Camera Mode */}
                <div style={{
                  background: 'rgba(0,0,0,0.6)',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  backdropFilter: 'blur(8px)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  minWidth: '120px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '11px', color: '#d1d5db', marginBottom: '4px' }}>CAMERA</div>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    color: viewMode === 'free' ? '#a855f7' :
                      (isControllingVehicle ? '#60a5fa' : '#4ade80')
                  }}>
                    {viewMode === 'free' ? 'FREE ORBIT' :
                      (isControllingVehicle ? 'FOLLOW' : 'ORBIT')}
                  </div>
                </div>
                
                {/* Rotors Status (only for helicopter) */}
                {mode === 'helicopter' && (
                  <div style={{
                    background: 'rgba(0,0,0,0.6)',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    backdropFilter: 'blur(8px)',
                    border: `1px solid ${rotorAnimation ? 'rgba(74, 222, 128, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
                    minWidth: '100px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '11px', color: '#d1d5db', marginBottom: '4px' }}>ROTORS</div>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: 'bold',
                      textTransform: 'uppercase',
                      color: rotorAnimation ? '#4ade80' : '#ef4444'
                    }}>
                      {rotorAnimation ? 'SPINNING' : 'STOPPED'}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Bottom right container - includes vehicle selector and controls */}
            <div style={{
              position: 'absolute',
              bottom: '16px',
              right: '16px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              gap: '12px',
              pointerEvents: 'none'
            }}>
              {/* Vehicle selector */}
              <div style={{
                background: 'rgba(0,0,0,0.6)',
                padding: '8px',
                borderRadius: '8px',
                backdropFilter: 'blur(8px)',
                border: '1px solid rgba(255,255,255,0.1)',
                display: 'flex',
                gap: '4px',
                pointerEvents: 'auto'
              }}>
                <button
                  onClick={() => setMode('helicopter')}
                  style={{
                    background: mode === 'helicopter' ? '#3b82f6' : 'rgba(255,255,255,0.1)',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: 'none',
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    if (mode !== 'helicopter') {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (mode !== 'helicopter') {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                    }
                  }}
                >
                  🚁 HELI
                </button>
                <button
                  onClick={() => setMode('car')}
                  style={{
                    background: mode === 'car' ? '#3b82f6' : 'rgba(255,255,255,0.1)',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: 'none',
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    if (mode !== 'car') {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (mode !== 'car') {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                    }
                  }}
                >
                  🚗 CAR
                </button>
                <button
                  onClick={() => setMode('avatar')}
                  style={{
                    background: mode === 'avatar' ? '#3b82f6' : 'rgba(255,255,255,0.1)',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: 'none',
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    if (mode !== 'avatar') {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (mode !== 'avatar') {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                    }
                  }}
                >
                  👤 AVATAR
                </button>
              </div>

              {/* Controls description */}
              <div style={{
                background: 'rgba(0,0,0,0.7)',
                padding: '12px 16px',
                borderRadius: '8px',
                backdropFilter: 'blur(8px)',
                color: 'white',
                maxWidth: '320px',
                pointerEvents: 'auto',
                fontSize: '11px',
                border: '1px solid rgba(255,255,255,0.1)'
              }}>
                {viewMode === 'vehicle' ? (
                  <div>
                    <p style={{
                      fontWeight: 'bold',
                      color: mode === 'helicopter' ? '#60a5fa' :
                             mode === 'car' ? '#fbbf24' : '#ec4899',
                      marginBottom: '6px',
                      fontSize: '12px'
                    }}>
                      {mode === 'helicopter' ? '🚁 HELICOPTER' :
                       mode === 'car' ? '🚗 CAR' :
                       '👤 AVATAR'}
                      {!isControllingVehicle && <span style={{ color: '#4ade80', fontSize: '10px' }}> • ORBIT</span>}
                    </p>
                    
                    {!isControllingVehicle && (
                      <div style={{
                        marginBottom: '8px',
                        padding: '4px 6px',
                        background: 'rgba(74, 222, 128, 0.15)',
                        borderRadius: '4px',
                        border: '1px solid rgba(74, 222, 128, 0.3)',
                        fontSize: '10px'
                      }}>
                        <span style={{ color: '#4ade80' }}>
                          Drag to orbit • Scroll to zoom
                        </span>
                      </div>
                    )}
                    
                    {mode === 'helicopter' ? (
                      <>
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr',
                          gap: '4px 8px',
                          marginBottom: '6px'
                        }}>
                          <div><span style={{ color: '#9ca3af' }}>R:</span> {rotorAnimation ? 'Stop' : 'Start'} Rotors</div>
                          <div><span style={{ color: '#9ca3af' }}>W/↑:</span> Ascend</div>
                          <div><span style={{ color: '#9ca3af' }}>S/↓:</span> Descend</div>
                          <div><span style={{ color: '#9ca3af' }}>A/←:</span> Left</div>
                          <div><span style={{ color: '#9ca3af' }}>D/→:</span> Right</div>
                          <div><span style={{ color: '#9ca3af' }}>Q/E:</span> Forward/Back</div>
                          <div><span style={{ color: '#9ca3af' }}>Z/X:</span> Rotate</div>
                          <div><span style={{ color: '#9ca3af' }}>C:</span> Free Orbit</div>
                        </div>
                        <div style={{
                          padding: '4px 6px',
                          background: rotorAnimation ? 'rgba(74, 222, 128, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                          borderRadius: '4px',
                          border: `1px solid ${rotorAnimation ? 'rgba(74, 222, 128, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
                          fontSize: '10px'
                        }}>
                          <span style={{ color: rotorAnimation ? '#4ade80' : '#ef4444' }}>
                            {rotorAnimation ? '✓ Rotors spinning' : '✗ Press R to start'}
                          </span>
                        </div>
                      </>
                    ) : mode === 'car' ? (
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '4px 8px'
                      }}>
                        <div><span style={{ color: '#9ca3af' }}>W/↑:</span> Accelerate</div>
                        <div><span style={{ color: '#9ca3af' }}>S/↓:</span> Brake</div>
                        <div><span style={{ color: '#9ca3af' }}>A/←:</span> Steer Left</div>
                        <div><span style={{ color: '#9ca3af' }}>D/→:</span> Steer Right</div>
                        <div><span style={{ color: '#9ca3af' }}>SHIFT:</span> Boost</div>
                        <div><span style={{ color: '#9ca3af' }}>C:</span> Free Orbit</div>
                      </div>
                    ) : (
                      <>
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr',
                          gap: '4px 8px',
                          marginBottom: '6px'
                        }}>
                          <div><span style={{ color: '#9ca3af' }}>W/↑:</span> Walk Forward</div>
                          <div><span style={{ color: '#9ca3af' }}>S/↓:</span> Walk Back</div>
                          <div><span style={{ color: '#9ca3af' }}>A/←:</span> Turn Left</div>
                          <div><span style={{ color: '#9ca3af' }}>D/→:</span> Turn Right</div>
                          <div><span style={{ color: '#9ca3af' }}>SPACE:</span> Jump</div>
                          <div><span style={{ color: '#9ca3af' }}>CTRL:</span> Crouch</div>
                          <div><span style={{ color: '#9ca3af' }}>SHIFT:</span> Run</div>
                          <div><span style={{ color: '#9ca3af' }}>C:</span> Free Orbit</div>
                        </div>
                        <div style={{
                          padding: '4px 6px',
                          background: 'rgba(168, 85, 247, 0.15)',
                          borderRadius: '4px',
                          border: '1px solid rgba(168, 85, 247, 0.3)',
                          fontSize: '10px'
                        }}>
                          <span style={{ color: '#a855f7' }}>
                            👁️ First-person view • Explore the world on foot
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <div>
                    <p style={{ fontWeight: 'bold', color: '#60a5fa', marginBottom: '6px', fontSize: '12px' }}>
                      🎥 FREE ORBIT
                    </p>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr',
                      gap: '4px',
                      fontSize: '10px'
                    }}>
                      <div><span style={{ color: '#9ca3af' }}>Left Drag:</span> Rotate</div>
                      <div><span style={{ color: '#9ca3af' }}>Right Drag:</span> Pan</div>
                      <div><span style={{ color: '#9ca3af' }}>Scroll:</span> Zoom</div>
                      <div><span style={{ color: '#9ca3af' }}>C:</span> Return to Vehicle</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  export default EnhancedDrivingGame;

